import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchEnquiry,
  getEnquiryOverrides,
  subscribeToEnquiries,
} from "../services/enquiriesService";
import type { EnquiryRecord } from "../types/enquiries.types";

/**
 * `missing` is its own outcome rather than a kind of failure: a 404 is the
 * server answering, and the screen has something definite to say about it.
 * Everything else — offline, 403, 500 — is `failed`, and worth retrying.
 */
export type EnquiryLoadStatus = "loading" | "ready" | "missing" | "failed";

export interface EnquiryState {
  /** The enquiry, once it has arrived. `undefined` in every other state. */
  enquiry: EnquiryRecord | undefined;
  /**
   * Where the record is. Read this before `enquiry`: a screen that only checks
   * for `undefined` says "Enquiry not found" about one still on its way.
   */
  status: EnquiryLoadStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
  /**
   * Reads the enquiry again without emptying the screen first — for a field
   * the backend moved on its own, where the page is right about everything
   * else and only wants the server's newer answer laid over it.
   *
   * A read that fails leaves what is on screen alone: it was correct a moment
   * ago, and replacing a working page with an error over a refresh nobody
   * asked for would lose more than it says.
   */
  refresh: () => void;
}

export const enquiryQueryKey = (id: string) => ["enquiries", "detail", id] as const;

/**
 * One enquiry, by the `public_id` in the route.
 *
 * Fetched, with the changes made in this sitting laid on top — so a stage
 * moved on the list is the stage this screen reads at, without going back to
 * the server for what was just saved to it.
 *
 * Backed by TanStack Query: revisiting an enquiry already opened this session
 * shows it immediately while a fresh read runs behind it. `reload` and
 * `refresh` both just ask the query to run again — the "quiet" behaviour
 * `refresh` used to need a flag for falls out for free here, since TanStack
 * Query keeps the last good `enquiry` on screen through a refetch on its own;
 * only a fetch with nothing cached at all (the failed screen's retry) ever
 * clears it or raises a toast.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useEnquiry(id: string): EnquiryState {
  const getToken = useTokenGetter();
  const toast = useToast();

  const overrides = useSyncExternalStore(
    subscribeToEnquiries,
    getEnquiryOverrides,
    getEnquiryOverrides
  );

  const query = useQuery({
    queryKey: enquiryQueryKey(id),
    queryFn: ({ signal }) => fetchEnquiry(getToken, id, signal),
    enabled: Boolean(id),
  });

  const missing404 = query.error instanceof ApiError && query.error.status === 404;

  useEffect(() => {
    if (!query.isError) return;

    // A quiet read is one the screen did not ask for and is not waiting on:
    // the enquiry it is already showing stays, down to the toast it does not
    // raise. Only the console hears about it.
    if (query.data !== undefined) {
      console.error("enquiry failed to refresh:", query.error);
      return;
    }

    // An enquiry that is not there is not an error to report — the screen
    // says so itself, and a toast on top of that is noise.
    if (missing404) return;

    console.error("enquiry failed to load:", query.error);
    toast.error(
      "Could not load this enquiry",
      query.error instanceof ApiError
        ? query.error.message
        : "We could not reach the server. Check your connection and try again."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error, query.data]);

  const enquiry = useMemo(() => {
    const record = query.data;
    if (!record) return undefined;

    const override = overrides[record.id];
    if (!override) return record;

    // Only the fields something on a screen has moved: an override holding
    // neither leaves the record exactly as it arrived.
    return {
      ...record,
      status: override.status ?? record.status,
      statusLabel: override.statusLabel ?? record.statusLabel,
      payment: override.payment ?? record.payment,
    };
  }, [query.data, overrides]);

  const status: EnquiryLoadStatus = !id
    ? "missing"
    : query.data !== undefined
    ? "ready"
    : query.isError
    ? missing404
      ? "missing"
      : "failed"
    : "loading";

  const reload = () => {
    void query.refetch();
  };

  return { enquiry, status, reload, refresh: reload };
}
